import { useState } from "react";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES, ISSUE_TYPES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  externalReferenceId,
  getDocumentTypeByContentType,
} from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";
import { useGlobalManagerApproval } from "@mibuildweb/components";
import { MATERIAL_SOURCE_VALUES } from "./materialConstants";

const buildIssueResultUpsertData = ({
  issue,
  attemptId,
  assignmentId,
  materialReferences,
}) => {
  const { issueResult } = issue;

  return {
    id: issueResult.id,
    issueId: issue.id,
    attemptId,
    type: issueResult.type ?? issue.type ?? ISSUE_TYPES.RECORDABLE,
    status: issueResult.status,
    title: issueResult.title,
    originalDescription: issueResult.originalDescription,
    description: issueResult.description,
    repairDescription: issueResult.repairDescription,
    assigneeId: issue.assignee?.id ?? null,
    issueTemplateId: issueResult.issueTemplateId,
    causedByTradeId: issueResult.causedByTradeId,
    resolvedByTradeId: issueResult.resolvedByTradeId,
    workCompleteDate: issueResult.workCompleteDate,
    validatedDate: issueResult.validatedDate,
    closedDate: issueResult.closedDate,
    images: issueResult.images,
    areaName: issueResult.areaName,
    assignmentId: issue.assignmentId ?? assignmentId,
    diagnosedIssue: issueResult.diagnosedIssue,
    submittedByUserId: issueResult.submittedByUserId,
    materials: issue.materials,
    isStandalone: issue.isStandalone,
    reviewDescription: issueResult.reviewDescription,
    incompleteReason: issueResult.incompleteReason,
    isBillable: issueResult.isBillable,
    reviewedByDispatcher: issueResult.reviewedByDispatcher,
    verificationRequest: issueResult.verificationRequest,
    verificationResponse: issueResult.verificationResponse,
    materialReferences,
  };
};

const normalizeMaterial = ({
  materialId,
  brand,
  description,
  quantity,
  source,
  requiresReplacement,
}) => ({
  materialId,
  brand: brand.trim(),
  description: description.trim(),
  quantity: Number(quantity),
  source,
  isUsed: source !== MATERIAL_SOURCE_VALUES.ORDER,
  requiresReplacement:
    requiresReplacement === true || requiresReplacement === "true",
});

const toMaterialReferenceRequest = (material) => ({
  materialId: material.materialId,
  brand: material.brand,
  description: material.description,
  quantity: material.quantity,
  source: material.source,
  isUsed: material.isUsed,
  requiresReplacement: material.requiresReplacement,
});

export const applyMaterialChanges = (
  materials,
  materialChanges,
  issueId = null
) => {
  const materialMap = new Map(
    materials.map((material) => [
      material.materialId,
      { ...material, issueId: issueId ?? material.issueId },
    ])
  );

  materialChanges
    .filter((material) => !issueId || material.issueId === issueId)
    .forEach((material) => {
      if (material.isDeleted) {
        materialMap.delete(material.materialId);
        return;
      }

      materialMap.set(material.materialId, material);
    });

  return [...materialMap.values()];
};

export const useCloseoutMaterialViewModel = ({
  issues = [],
  attemptId,
  assignmentId,
  getAttempt = () => {},
  setIsMaterialChecked = () => {},
}) => {
  const stateManager = useComponentState();
  const { executeApiRequest } = useGlobalManagerApproval();
  const {
    loading: isDocumentLoading,
    removeDocument,
    uploadDocument,
  } = useDocumentsViewModel();
  const [materialChanges, setMaterialChanges] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materialPendingDelete, setMaterialPendingDelete] = useState(null);
  const [isMaterialModalOpen, setIsMaterialModalOpen] = useState(false);

  const openAddMaterialModal = () => {
    setSelectedMaterial(null);
    setIsMaterialModalOpen(true);
  };
  const openEditMaterialModal = (material) => {
    setSelectedMaterial(material);
    setIsMaterialModalOpen(true);
  };
  const closeMaterialModal = () => {
    setIsMaterialModalOpen(false);
    setSelectedMaterial(null);
  };
  const openDeleteMaterialModal = (material) =>
    setMaterialPendingDelete(material);
  const closeDeleteMaterialModal = () => setMaterialPendingDelete(null);

  const getIssue = (issueId) => {
    const issue = issues.find((candidate) => candidate.id === issueId);

    if (!issue?.issueResult?.id) {
      toastService.error({
        text: "Unable to update material",
        subtext: "The selected task has no result for this attempt.",
      });
      return null;
    }

    return issue;
  };

  const getCurrentIssueMaterials = (issue) =>
    applyMaterialChanges(
      issue.materialReferences ?? [],
      materialChanges,
      issue.id
    );

  const saveMaterialChange = (material) =>
    setMaterialChanges((currentMaterials) => [
      ...currentMaterials.filter(
        (currentMaterial) => currentMaterial.materialId !== material.materialId
      ),
      material,
    ]);

  const updateMaterialAttachment = async ({
    documentToDelete,
    existingDocuments,
    file,
    materialId,
  }) => {
    let documents = existingDocuments;

    const uploadedDocument = file
      ? await uploadDocument({
          file,
          documentType: getDocumentTypeByContentType(file.type),
          externalReferenceId: externalReferenceId.getTaskMaterialRefId({
            materialId,
          }),
        })
      : null;

    if (file && !uploadedDocument) {
      return { documents, hasError: true };
    }

    if (documentToDelete) {
      const wasRemoved = await removeDocument(documentToDelete);
      if (!wasRemoved) {
        return {
          documents: uploadedDocument
            ? [uploadedDocument, ...documents]
            : documents,
          hasError: true,
        };
      }

      documents = documents.filter(
        (document) => document.id !== documentToDelete.id
      );
    }

    return uploadedDocument
      ? { documents: [uploadedDocument] }
      : { documents };
  };

  const upsertIssueMaterials = async ({
    issue,
    materialReferences,
    callback,
  }) => {
    const data = buildIssueResultUpsertData({
      issue,
      attemptId,
      assignmentId,
      materialReferences: materialReferences.map(toMaterialReferenceRequest),
    });
    const options = await taskService.issuesResults.getUpsertOptions({ data });

    await executeApiRequest({
      options,
      toastMessageForManagerApproval: {
        text: "Manager Approval Required",
        subtext:
          "Updating this task requires manager approval before the materials can be changed.",
      },
      callback,
    });
  };

  const saveMaterial = ({ issueId, file, documentToDelete, ...materialForm }) =>
    stateManager.run(async () => {
      const issue = getIssue(issueId);
      if (!issue) {
        return;
      }

      const materialBeingEdited = selectedMaterial;
      const materialReference = {
        ...normalizeMaterial(materialForm),
        issueId,
      };
      const currentMaterials = getCurrentIssueMaterials(issue);
      const nextMaterials = materialBeingEdited
        ? currentMaterials.map((material) =>
            material.materialId === materialReference.materialId
              ? materialReference
              : material
          )
        : [...currentMaterials, materialReference];

      await upsertIssueMaterials({
        issue,
        materialReferences: nextMaterials,
        callback: async ({ status }) => {
          if (status !== HTTP_STATUSES.OK) {
            return;
          }

          const { documents, hasError: attachmentError } =
            await updateMaterialAttachment({
              documentToDelete,
              existingDocuments: materialBeingEdited?.documents ?? [],
              file,
              materialId: materialReference.materialId,
            });
          const savedMaterial = {
            ...materialReference,
            documents,
          };

          saveMaterialChange(savedMaterial);
          setIsMaterialChecked(false);
          closeMaterialModal();
          if (attachmentError) {
            toastService.error({
              text: "Material saved, but the attachment could not be updated.",
              subtext: "Please review the attached file.",
            });
            return;
          }

          toastService.success({
            text: materialBeingEdited
              ? "Material updated successfully."
              : "Material added successfully.",
          });
        },
      });
    });

  const deleteMaterial = () =>
    stateManager.run(async () => {
      if (!materialPendingDelete) {
        return;
      }

      const materialToDelete = materialPendingDelete;
      const issue = getIssue(materialToDelete.issueId);
      if (!issue) {
        return;
      }

      const response = await taskService.issues.deleteMaterialReference({
        id: issue.id,
        materialId: materialToDelete.materialId,
        modifiedOn: issue.modifiedOn,
        toastMessageConfig: {
          success: { text: "Material deleted successfully." },
          failure: { text: "Failed to delete material." },
        },
      });

      if (response.status !== HTTP_STATUSES.NO_CONTENT) {
        return;
      }

      saveMaterialChange({ ...materialToDelete, isDeleted: true });
      setIsMaterialChecked(false);
      closeDeleteMaterialModal();
      getAttempt();
    });

  return {
    closeMaterialModal,
    closeDeleteMaterialModal,
    deleteMaterial,
    isMaterialModalOpen,
    isDeleteMaterialModalOpen: Boolean(materialPendingDelete),
    isLoading: stateManager.isBusy() || isDocumentLoading,
    materialChanges,
    materialPendingDelete,
    openAddMaterialModal,
    openDeleteMaterialModal,
    openEditMaterialModal,
    saveMaterial,
    selectedMaterial,
  };
};
